import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { StudentAcademicView } from "@/components/academic/StudentAcademicView";
import { AnnouncementsManager } from "@/components/academic/AnnouncementsManager";
import { AcademicDocumentsManager } from "@/components/academic/AcademicDocumentsManager";
import { SessionLibraryManager } from "@/components/academic/SessionLibraryManager";
import { AcademicEligibilityRoster } from "@/components/academic/AcademicEligibilityRoster";
import { SessionCoverageTab } from "@/components/academic/SessionCoverageTab";
import { AttendanceEligibilityTab } from "@/components/academic/AttendanceEligibilityTab";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, BookOpen, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { canManageModule } from "@/lib/permissions";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/dashboard/StatusBadge";

interface CourseFormData {
  course_code: string;
  course_name: string;
  credits: number;
  semester: string;
  description: string;
  level: string;
  class: string;
}

const Academic = () => {
  const { roles } = useAuth();
  const canManage = canManageModule(roles, "academic");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showCourses, setShowCourses] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [regFormOpen, setRegFormOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any>(null);
  const [form, setForm] = useState<CourseFormData>({ course_code: "", course_name: "", credits: 3, semester: "", description: "", level: "", class: "" });
  const [regForm, setRegForm] = useState({ student_id: "", course_id: "", semester: "", academic_year: "" });

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      const { data, error } = await supabase.from("courses").select("*").order("course_code");
      if (error) throw error;
      return data;
    },
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ["registrations-with-details"],
    queryFn: async () => {
      const { data, error } = await supabase.from("registrations").select("*, students(first_name, last_name, student_number, financial_clearance), courses(course_code, course_name)").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: students = [] } = useQuery({
    queryKey: ["students-for-reg"],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("id, first_name, last_name, student_number, financial_clearance").eq("status", "active").order("first_name");
      return data ?? [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: CourseFormData & { id?: string }) => {
      if (data.id) {
        const { error } = await supabase.from("courses").update(data).eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("courses").insert(data);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      toast({ title: editingCourse ? "Course updated" : "Course added" });
      setFormOpen(false);
      setEditingCourse(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const regMutation = useMutation({
    mutationFn: async (data: typeof regForm) => {
      // Check financial clearance
      const student = students.find(s => s.id === data.student_id);
      if (!student?.financial_clearance) {
        throw new Error("Student does not have financial clearance. Please contact the Accounting department.");
      }
      const { error } = await supabase.from("registrations").insert(data);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["registrations-with-details"] });
      toast({ title: "Registration successful" });
      setRegFormOpen(false);
    },
    onError: (e: any) => toast({ title: "Registration Failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("courses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      toast({ title: "Course deleted" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const filtered = courses.filter(c =>
    `${c.course_code} ${c.course_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const openEdit = (c: any) => {
    setEditingCourse(c);
    setForm({ course_code: c.course_code, course_name: c.course_name, credits: c.credits || 3, semester: c.semester || "", description: c.description || "", level: c.level || "", class: c.class || "" });
    setFormOpen(true);
  };

  const openNew = () => {
    setEditingCourse(null);
    setForm({ course_code: "", course_name: "", credits: 3, semester: "", description: "", level: "", class: "" });
    setFormOpen(true);
  };

  const updateFormWithClass = (updates: Partial<CourseFormData>) => {
    setForm(f => {
      const next = { ...f, ...updates };
      next.class = next.course_code && next.level ? `${next.course_code}${next.level}` : "";
      return next;
    });
  };

  const selectedStudentForReg = students.find(s => s.id === regForm.student_id);

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1>Academic</h1>
            {canManage && <p className="text-muted-foreground text-xs sm:text-sm">Manage courses and student registration</p>}
          </div>
          {canManage && (
            <div className="page-header-actions">
              <Button variant="outline" size="sm" className="sm:size-default" onClick={() => { setRegForm({ student_id: "", course_id: "", semester: "", academic_year: "" }); setRegFormOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" />Register Student
              </Button>
              <Button size="sm" className="sm:size-default" onClick={openNew}><Plus className="mr-2 h-4 w-4" />Add Course</Button>
            </div>
          )}
        </div>

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingCourse ? "Edit Course" : "Add Course"}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate({ ...form, id: editingCourse?.id }); }} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Course Abbreviation *</Label><Input value={form.course_code} onChange={e => updateFormWithClass({ course_code: e.target.value })} required /></div>
                <div><Label>Course Name *</Label><Input value={form.course_name} onChange={e => setForm(f => ({ ...f, course_name: e.target.value }))} required /></div>
                <div><Label>Level *</Label>
                  <Select value={form.level} onValueChange={v => updateFormWithClass({ level: v })}>
                    <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="6">6</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Class (auto-generated)</Label><Input value={form.class} readOnly className="bg-muted" /></div>
                <div><Label>Semester</Label>
                  <Select value={form.semester} onValueChange={v => setForm(f => ({ ...f, semester: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="I">I</SelectItem>
                      <SelectItem value="II">II</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
              <Button type="submit" disabled={saveMutation.isPending} className="w-full">{saveMutation.isPending ? "Saving..." : "Save"}</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={regFormOpen} onOpenChange={setRegFormOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Register Student for Course</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); regMutation.mutate(regForm); }} className="space-y-4">
              <div>
                <Label>Student *</Label>
                <Select value={regForm.student_id} onValueChange={v => setRegForm(f => ({ ...f, student_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                  <SelectContent>
                    {students.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.first_name} {s.last_name} ({s.student_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedStudentForReg && !selectedStudentForReg.financial_clearance && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Student not financially cleared
                  </div>
                )}
                {selectedStudentForReg?.financial_clearance && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-primary">
                    <Badge variant="default">Financially Cleared</Badge>
                  </div>
                )}
              </div>
              <div>
                <Label>Course *</Label>
                <Select value={regForm.course_id} onValueChange={v => setRegForm(f => ({ ...f, course_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.course_code} - {c.course_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Semester *</Label><Input value={regForm.semester} onChange={e => setRegForm(f => ({ ...f, semester: e.target.value }))} required /></div>
                <div><Label>Academic Year *</Label><Input value={regForm.academic_year} onChange={e => setRegForm(f => ({ ...f, academic_year: e.target.value }))} required /></div>
              </div>
              <Button type="submit" disabled={regMutation.isPending || (!!selectedStudentForReg && !selectedStudentForReg.financial_clearance)} className="w-full">
                {regMutation.isPending ? "Registering..." : "Register"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {canManage ? (
          <Tabs defaultValue="announcements">
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="announcements">Announcements</TabsTrigger>
              <TabsTrigger value="documents">Timetable</TabsTrigger>
              <TabsTrigger value="registration">Registration</TabsTrigger>
              <TabsTrigger value="courses">Courses</TabsTrigger>
              <TabsTrigger value="session-library">Session Library</TabsTrigger>
              <TabsTrigger value="coverage">Session Coverage</TabsTrigger>
              <TabsTrigger value="eligibility-att">Eligibility</TabsTrigger>
            </TabsList>

            <TabsContent value="courses" className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex gap-3 w-full sm:flex-1">
                  <div className="relative w-full sm:flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search courses..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Label htmlFor="toggle-courses" className="text-sm text-muted-foreground">{showCourses ? "Hide" : "Show"} Courses</Label>
                  <Switch id="toggle-courses" checked={showCourses} onCheckedChange={setShowCourses} />
                </div>
              </div>
              {showCourses && (
                <Card>
                  <CardHeader>
                    <CardTitle>Course Catalog</CardTitle>
                    <CardDescription>{filtered.length} courses</CardDescription>
                  </CardHeader>
                  <CardContent className="table-responsive">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Abbreviation</TableHead>
                          <TableHead>Course Name</TableHead>
                          <TableHead>Level</TableHead>
                          <TableHead>Class</TableHead>
                          
                          <TableHead>Semester</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(c => (
                          <TableRow key={c.id}>
                            <TableCell className="font-mono">{c.course_code}</TableCell>
                            <TableCell className="font-medium">{c.course_name}</TableCell>
                            <TableCell>{c.level || "—"}</TableCell>
                            <TableCell className="font-mono">{c.class || "—"}</TableCell>
                            <TableCell>{c.semester || "—"}</TableCell>
                            <TableCell className="flex gap-1">
                              {canManage && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="h-3 w-3" /></Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="h-3 w-3 text-destructive" /></Button></AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader><AlertDialogTitle>Delete course?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteMutation.mutate(c.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{isLoading ? "Loading..." : "No courses found"}</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="registration" className="space-y-4">
              <Tabs defaultValue="registrations">
                <TabsList>
                  <TabsTrigger value="registrations">Course Registrations</TabsTrigger>
                  <TabsTrigger value="eligibility">Academic Eligibility</TabsTrigger>
                </TabsList>
                <TabsContent value="eligibility" className="space-y-4 pt-4">
                  <AcademicEligibilityRoster />
                </TabsContent>
                <TabsContent value="registrations" className="space-y-4 pt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Course Registrations</CardTitle>
                  <CardDescription>{registrations.length} registrations</CardDescription>
                </CardHeader>
                <CardContent className="table-responsive">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Course</TableHead>
                        <TableHead>Semester</TableHead>
                        <TableHead>Academic Year</TableHead>
                        <TableHead>Clearance</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registrations.map((r: any) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.students?.first_name} {r.students?.last_name}</TableCell>
                          <TableCell>{r.courses?.course_code} - {r.courses?.course_name}</TableCell>
                          <TableCell>{r.semester}</TableCell>
                          <TableCell>{r.academic_year}</TableCell>
                          <TableCell>
                            <Badge variant={r.students?.financial_clearance ? "default" : "secondary"}>
                              {r.students?.financial_clearance ? "Cleared" : "Pending"}
                            </Badge>
                          </TableCell>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                        </TableRow>
                      ))}
                      {registrations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No registrations</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
                </TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="announcements" className="space-y-4">
              <AnnouncementsManager />
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              <AcademicDocumentsManager />
            </TabsContent>

            <TabsContent value="session-library" className="space-y-4">
              <SessionLibraryManager />
            </TabsContent>

            <TabsContent value="coverage" className="space-y-4">
              <SessionCoverageTab />
            </TabsContent>

            <TabsContent value="eligibility-att" className="space-y-4">
              <AttendanceEligibilityTab />
            </TabsContent>
          </Tabs>
        ) : (
          <StudentAcademicView />
        )}
      </div>
    </AppLayout>
  );
};

export default Academic;
